import React, { Suspense, lazy } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useLocation,
} from 'react-router-dom';
import { useAppSelector } from 'redux/hooks';
import {
  selectPermission, selectToken, selectDiscreteAccess,
} from 'redux/global';
import Layout from 'pages/Layout/Layout';
import Loader from 'pages/components/Loader/Loader';
import { getRouteLoadingMessage } from 'pages/components/Loader/getRouteLoadingMessage';
import React18BrowserRouter from 'routing/React18BrowserRouter';
import {
  PERM,
  canAccessCrm,
  canAccessCashierSalesTabs,
  canAccessDashboard,
  canAccessDepartmentsPage,
  canAccessInventoryPage,
  canAccessMetrics,
} from 'pages/Settings/redux';
import { AnalyticsTracker } from 'utils/analytics';
import { ChatwootBubbleGate } from 'utils/chatwootBubble';
import { HOSPITALITY_PATHS, erpSettingsRoutePaths } from 'pages/Layout/hospitalityModulePaths';

const Hospitality = lazy(() => import('pages/Hospitality/Hospitality'));
const RoomActions = lazy(() => import('pages/Hospitality/components/RoomActions'));
const ReserveRooms = lazy(() => import('pages/ReserveRooms/ReserveRooms'));
const Reservations = lazy(() => import('pages/Reservations/Reservations'));
const Logs = lazy(() => import('pages/Logs/Logs'));
const Branches = lazy(() => import('pages/Branches/Branches'));
const Inventory = lazy(() => import('pages/Inventory/Inventory'));
const SalesPage = lazy(() => import('pages/Sales/Sales'));
const RefundsPage = lazy(() => import('pages/Sales/Refunds'));
const Metrics = lazy(() => import('pages/Metrics/Metrics'));
const Reporting = lazy(() => import('pages/Reporting/Reporting'));
const Settings = lazy(() => import('pages/Settings/Settings'));
const RoomSetup = lazy(() => import('pages/Hospitality/RoomSetup'));
const CRM = lazy(() => import('pages/CRM/CRM'));
const WalkIns = lazy(() => import('pages/WalkIns/WalkIns'));
const FeedbackChat = lazy(() => import('pages/CRM/components/FeedbackChat'));
const FeedbackRating = lazy(() => import('pages/FeedbackRating/FeedbackRating'));
const NotFound = lazy(() => import('pages/NotFound/NotFound'));
const Forgot = lazy(() => import('pages/Forgot/Forgot'));
const SetPassword = lazy(() => import('pages/SetPassword/SetPassword'));
const RegisterPassword = lazy(() => import('pages/RegisterPassword/RegisterPassword'));
const Insights = lazy(() => import('pages/Insights/Insights'));
const SignIn = lazy(() => import('pages/SignIn/SignIn'));
const SignUp = lazy(() => import('pages/SignUp/SignUp'));
const Live = lazy(() => import('pages/Live/Live'));
const AddHotel = lazy(() => import('pages/AddHotel/AddHotel'));
const UnSubscribe = lazy(() => import('pages/UnSubscribe/UnSubscribe'));

(document.body.style as any).zoom = '100.06%';

const RouteChunkFallback = function RouteChunkFallback() {
  // After session boot, lazy chunks still suspend on first visit — show the
  // in-app loader instead of an empty (white) page. Session boot already
  // overlays Loader; stacking the same overlay is a no-op visually.
  const { pathname } = useLocation();
  return <Loader instant message={getRouteLoadingMessage(pathname)} />;
};

const Routing = function Routing() {
  const token = useAppSelector(selectToken);
  const permission = useAppSelector(selectPermission);
  const discreteAccess = useAppSelector(selectDiscreteAccess);

  return (
    <React18BrowserRouter>
      <AnalyticsTracker />
      <ChatwootBubbleGate />
      <Layout>
        {/* In-app loader only — never the refresh logo splash */}
        <Suspense fallback={<RouteChunkFallback />}>
          <Switch>
            <Route exact path="/">
              <SignIn />
            </Route>
            <Route exact path="/pricing">
              <SignIn />
            </Route>
            <Route exact path="/unsubscribe">
              <UnSubscribe />
            </Route>
            <Route exact path="/feedback/1/:param">
              <FeedbackRating />
            </Route>
            <Route exact path="/forgot">
              <Forgot />
            </Route>
            <Route exact path="/forgot/:key/:id">
              <SetPassword />
            </Route>
            <Route exact path="/register/:key/:id/:emailEnc?">
              <RegisterPassword />
            </Route>
            <Route exact path="/signin">
              <SignIn />
            </Route>
            <Route exact path="/live">
              <Live />
            </Route>
            <Route exact path="/signup">
              <SignUp />
            </Route>
            <Route exact path="/addH/:id">
              <AddHotel />
            </Route>
            {/* Legacy URLs → ERP hospitality module (first segment). */}
            <Route
              exact
              path="/rooms"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.operations, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/rooms/actions/:id"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.operationsActions(match.params.id),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality"
              render={({ location }) => (
                <Redirect
                  to={{
                    pathname: canAccessDashboard(permission)
                      ? HOSPITALITY_PATHS.dashboard
                      : HOSPITALITY_PATHS.operations,
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality/actions/:id"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.operationsActions(match.params.id),
                    search: location.search,
                  }}
                />
              )}
            />
            {/* Pages that moved out of the Hospitality URL prefix. */}
            <Route
              exact
              path="/hospitality/sales"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.sales, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/hospitality/refunds"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.refunds, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/hospitality/departments"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.departments, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/hospitality/departments/:subtab"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.departmentsSub(match.params.subtab),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality/inventory"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.inventory, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/hospitality/audits"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.audits, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/hospitality/audits/:action"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.auditsAction(match.params.action),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality/metrics"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.metrics, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/hospitality/crm/:tab/:messageId"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.crmMessage(
                      match.params.tab === 'settings' ? 'recipients' : match.params.tab,
                      match.params.messageId,
                    ),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality/crm/:tab"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.crmTab(
                      match.params.tab === 'settings' ? 'recipients' : match.params.tab,
                    ),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality/crm"
              render={({ location }) => (
                <Redirect
                  to={{ pathname: HOSPITALITY_PATHS.crmTab('customers'), search: location.search }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality/settings/editroom/:id"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.roomsEditRoom(match.params.id),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality/settings/add-rooms"
              render={({ location }) => (
                <Redirect
                  to={{ pathname: HOSPITALITY_PATHS.roomsTab('add-rooms'), search: location.search }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality/settings/room-types"
              render={({ location }) => (
                <Redirect
                  to={{ pathname: HOSPITALITY_PATHS.roomsTab('room-types'), search: location.search }}
                />
              )}
            />
            <Route
              exact
              path="/hospitality/settings/rate-plans"
              render={({ location }) => (
                <Redirect
                  to={{ pathname: HOSPITALITY_PATHS.roomsTab('rate-plans'), search: location.search }}
                />
              )}
            />
            <Route
              exact
              path="/sales-distribution"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.departments, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/reporting-analytics"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.audits, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/logs"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.audits, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/logs/:action"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.auditsAction(match.params.action),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/branches"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.departments, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/branches/:subtab"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.departmentsSub(match.params.subtab),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/dashboard"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.dashboard, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/sales"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.sales, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/refunds"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.refunds, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/reservations"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.reservations, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/walk-ins"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.walkIns, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/departments"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.departments, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/departments/:subtab"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.departmentsSub(match.params.subtab),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/inventory"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.inventory, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/audits"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.audits, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/audits/:action"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.auditsAction(match.params.action),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/metrics"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.metrics, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/settings/editroom/:id"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.settingsEditRoom(match.params.id),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/settings/:subtab/:plan"
              render={({ match, location }) => (
                <Redirect
                  to={{
                    pathname: HOSPITALITY_PATHS.settingsPlan(match.params.subtab, match.params.plan),
                    search: location.search,
                  }}
                />
              )}
            />
            <Route
              exact
              path="/settings/:subtab"
              render={({ match, location }) => {
                const { subtab } = match.params;
                const roomSetupTabs = ['add-rooms', 'room-types', 'rate-plans'];
                const pathname = roomSetupTabs.includes(subtab)
                  ? HOSPITALITY_PATHS.roomsTab(subtab)
                  : HOSPITALITY_PATHS.settingsSub(subtab);
                return <Redirect to={{ pathname, search: location.search }} />;
              }}
            />
            <Route
              exact
              path="/settings"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.settings, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/reserve"
              render={({ location }) => (
                <Redirect to={{ pathname: HOSPITALITY_PATHS.reserve, search: location.search }} />
              )}
            />
            <Route
              exact
              path="/profile"
              render={() => <Redirect to={HOSPITALITY_PATHS.settingsAccount} />}
            />
            <Route exact path={[
              HOSPITALITY_PATHS.operations,
              `${HOSPITALITY_PATHS.operations}/actions/:id`,
            ]}
            >
              <Route exact path={HOSPITALITY_PATHS.operations}>
                <Hospitality />
              </Route>
              <Route exact path={`${HOSPITALITY_PATHS.operations}/actions/:id`}>
                <RoomActions />
              </Route>
            </Route>
            {
              (permission || 0) >= PERM.FRONT_DESK && (
                <Route exact path={HOSPITALITY_PATHS.reservations}>
                  <Reservations />
                </Route>
              )
            }
            {
              token && (
                <Route exact path={HOSPITALITY_PATHS.walkIns}>
                  <WalkIns />
                </Route>
              )
            }
            {
              token && (
                <Route exact path={HOSPITALITY_PATHS.audits}>
                  <Logs />
                </Route>
              )
            }
            {
              token && canAccessDepartmentsPage(permission) && (
                <Route exact path={[
                  HOSPITALITY_PATHS.departments,
                  `${HOSPITALITY_PATHS.departments}/:subtab`,
                ]}
                >
                  <Branches />
                </Route>
              )
            }
            {
              token && canAccessCashierSalesTabs(permission) && (
                <Route exact path={HOSPITALITY_PATHS.sales}>
                  <SalesPage />
                </Route>
              )
            }
            {
              token && canAccessCashierSalesTabs(permission) && (
                <Route exact path={HOSPITALITY_PATHS.refunds}>
                  <RefundsPage />
                </Route>
              )
            }
            {
              token && canAccessInventoryPage(permission, discreteAccess) && (
                <Route exact path={HOSPITALITY_PATHS.inventory}>
                  <Inventory />
                </Route>
              )
            }
            {
              token && (permission || 0) >= PERM.FRONT_DESK_2 && (
                <Route exact path={`${HOSPITALITY_PATHS.rooms}/editroom/:id`}>
                  <RoomSetup />
                </Route>
              )
            }
            {
              token && (permission || 0) >= PERM.SUB_MANAGER && (
                <Route exact path={[
                  HOSPITALITY_PATHS.rooms,
                  `${HOSPITALITY_PATHS.rooms}/:subtab`,
                ]}
                >
                  <Route exact path={`${HOSPITALITY_PATHS.rooms}/:subtab`}>
                    <RoomSetup />
                  </Route>
                  <Route exact path={HOSPITALITY_PATHS.rooms}>
                    <RoomSetup />
                  </Route>
                </Route>
              )
            }
            {
              token && (
                <Route exact path={erpSettingsRoutePaths()}>
                  <Settings />
                </Route>
              )
            }
            {
              token && canAccessCrm(permission) && (
              <Route exact path={[
                HOSPITALITY_PATHS.crm,
                `${HOSPITALITY_PATHS.crm}/:tab`,
                `${HOSPITALITY_PATHS.crm}/:tab/:messageId`,
              ]}
              >
                <Route exact path={`${HOSPITALITY_PATHS.crm}/:tab/:messageId`}>
                  <FeedbackChat />
                </Route>
                <Route exact path={`${HOSPITALITY_PATHS.crm}/:tab`}>
                  <CRM />
                </Route>
                <Route exact path={HOSPITALITY_PATHS.crm}>
                  <CRM />
                </Route>
              </Route>
              )
            }
            {
              token && (permission || 0) >= PERM.FRONT_DESK && (
                <Route exact path={HOSPITALITY_PATHS.reserve}>
                  <ReserveRooms />
                </Route>
              )
            }
            {
              token && canAccessDashboard(permission) && (
                <Route exact path={HOSPITALITY_PATHS.dashboard}>
                  <Insights />
                </Route>
              )
            }
            {
              /* Associate Manager+ can open timed /audits/:action (e.g. Compliance review). */
              token && (permission || 0) >= PERM.SUB_MANAGER && (
                <Route exact path={`${HOSPITALITY_PATHS.audits}/:action`}>
                  <Logs />
                </Route>
              )
            }
            {
              token && canAccessMetrics(permission) && (
                <Route exact path={HOSPITALITY_PATHS.metrics}>
                  <Metrics />
                </Route>
              )
            }
            {
              token && (
                <Route
                  exact
                  path={[
                    HOSPITALITY_PATHS.reporting,
                    `${HOSPITALITY_PATHS.reporting}/:subtab`,
                  ]}
                >
                  <Reporting />
                </Route>
              )
            }
            <Route exact path="*">
              <NotFound />
            </Route>
          </Switch>
        </Suspense>
      </Layout>
    </React18BrowserRouter>
  );
};

export default Routing;
